/**
 * Priming — replica PrimingTest.aspx.cs.
 *
 * Mecánica (reimplementada en CSS/texto puro, ya no con GIFs pre-generados):
 * - Frame 1 (PRIME): se muestra el facilitador (valor_enmascardo) solo, durante
 *   tiempo_miliPalabra.
 * - Frame 2 (MASK): el mismo facilitador con un patrón de ruido superpuesto,
 *   durante tiempo_milimascara.
 * - Frame 3 / RESPONSE: se muestra la palabra objetivo (Valor) y el usuario
 *   responde Sí/No (¿reconoció la palabra?). Sin límite de tiempo — el original
 *   mostraba este frame 1000ms fijo antes de la pregunta, pero acá el usuario
 *   ya ve la palabra mientras responde, no hace falta el delay extra.
 * - Se mide RT desde que arranca RESPONSE hasta el clic.
 * - Resultados agrupados por facilitador (SI / NO) → RT promedio de trials correctos.
 *
 * El legacy generaba un GIF animado por combinación (secuencia, tiempo_milimascara)
 * combinando 3 bitmaps con un encoder; se abandonó ese enfoque porque tiempo_milimascara
 * quedaba "horneado" en un archivo estático (no se podía reconfigurar sin regenerar
 * el GIF) y porque esos GIFs pre-generados podían quedar desactualizados si el
 * Valor/valor_enmascardo de la Secuencia se editaba después. Acá se lee todo en vivo.
 */
import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getTestConfig, TYPE_TO_API_PATH } from '../../api/client';
import TestLayout, {
  DoneScreen, YesNoButtons, LoadingScreen, ErrorScreen, useSpeedMultiplier,
} from './shared/TestLayout';
import SaveResults from './shared/SaveResults';
import { navigateAfterTest } from '../../api/ensayosClient';
import SafeHtml from './shared/SafeHtml';
import { registrarUso } from '../../api/usoClient';

const PHASE = { LOADING: 'loading', PRIME: 'prime', MASK: 'mask', RESPONSE: 'response', DONE: 'done' };
const DEFAULT_TIEMPO_PALABRA = 300;
const DEFAULT_TIEMPO_MASCARA = 250;

const WORD_CLASS = 'text-4xl font-bold text-center text-slate-800 max-w-2xl leading-tight';

/** Palabra enmascarada con el mismo patrón de ruido (dígitos apilados) que usaba el legacy. */
function MaskedWord({ html }) {
  return (
    <div className="relative flex items-center justify-center min-h-[12rem]">
      <div
        aria-hidden
        className="absolute inset-0 flex flex-col items-center justify-center gap-1 select-none pointer-events-none text-slate-400"
        style={{ fontSize: '2rem', lineHeight: 1.2 }}
      >
        <span>12345678</span>
        <span>12345678</span>
        <span>12345678</span>
      </div>
      <SafeHtml as="div" html={html} className={`relative ${WORD_CLASS}`} />
    </div>
  );
}

export default function TestRunPriming() {
  const { lenguajeId, categoriaId, tareaId, type, testId } = useParams();
  const navigate = useNavigate();
  const apiType = TYPE_TO_API_PATH[type] ?? type;

  const speedMultiplier = useSpeedMultiplier();
  const [config, setConfig] = useState(null);
  const [error, setError] = useState(null);
  const [phase, setPhase] = useState(PHASE.LOADING);
  const [seqIndex, setSeqIndex] = useState(0);
  const [responses, setResponses] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const rtStartRef     = useRef(null);
  const timerRef       = useRef(null);
  const usageLoggedRef = useRef(false);
  const responsesRef   = useRef([]);
  responsesRef.current = responses;

  useEffect(() => {
    getTestConfig(apiType, testId)
      .then((data) => { setConfig(data); setPhase(PHASE.PRIME); })
      .catch((e) => setError(e.message));
  }, [apiType, testId]);

  const tiempoMiliPalabra = config?.tiempo_miliPalabra || DEFAULT_TIEMPO_PALABRA;
  const tiempoMilimascara = config?.tiempo_milimascara || DEFAULT_TIEMPO_MASCARA;

  // PRIME -> MASK -> RESPONSE, con los tiempos configurados para este Priming
  useEffect(() => {
    if (phase === PHASE.PRIME) {
      timerRef.current = setTimeout(() => setPhase(PHASE.MASK), tiempoMiliPalabra / speedMultiplier);
    } else if (phase === PHASE.MASK) {
      timerRef.current = setTimeout(() => {
        rtStartRef.current = performance.now();
        setPhase(PHASE.RESPONSE);
      }, tiempoMilimascara / speedMultiplier);
    } else {
      return;
    }
    return () => clearTimeout(timerRef.current);
  }, [phase, seqIndex, speedMultiplier, tiempoMiliPalabra, tiempoMilimascara]);

  const goBack = useCallback(() => {
    navigateAfterTest(
      navigate,
      `/lenguaje/${lenguajeId}/categoria/${categoriaId}/tarea/${tareaId}`,
    );
  }, [navigate, lenguajeId, categoriaId, tareaId]);

  useEffect(() => {
    if (phase !== PHASE.DONE || usageLoggedRef.current) return;
    usageLoggedRef.current = true;
    const r = responsesRef.current;
    registrarUso({
      tipo_test:   `P${testId}`,
      test_id:     testId,
      tarea_id:    tareaId,
      nombre_test: config?.descripcion ?? '',
      completada:  true,
      total_items: r.length,
      correctas:   r.filter((x) => x.correcto).length,
      errores:     r.filter((x) => !x.correcto).length,
    });
  }, [phase, testId, tareaId, config]);

  const handleExit = useCallback(() => {
    if (!usageLoggedRef.current) {
      usageLoggedRef.current = true;
      const r = responsesRef.current;
      registrarUso({
        tipo_test:   `P${testId}`,
        test_id:     testId,
        tarea_id:    tareaId,
        nombre_test: config?.descripcion ?? '',
        completada:  false,
        total_items: r.length,
        correctas:   r.filter((x) => x.correcto).length,
        errores:     r.filter((x) => !x.correcto).length,
      });
    }
    goBack();
  }, [goBack, testId, tareaId, config]);

  const secuencia = config?.ListaValores?.Secuencia ?? [];
  const conclusiones = config?.Concluciones ?? '';
  const currentItem = secuencia[seqIndex];

  const handleResponse = useCallback((userYes) => {
    const rt = Math.round(performance.now() - (rtStartRef.current ?? 0));
    const correct = currentItem.es_verdadero === userYes;
    setResponses((r) => [...r, {
      valor: currentItem.Valor ?? '',
      facilitador: currentItem.facilitador ? 'SI' : 'NO',
      respuesta: userYes ? 'SI' : 'NO',
      correcto: correct,
      rt,
    }]);
    const next = seqIndex + 1;
    if (next >= secuencia.length) { setPhase(PHASE.DONE); return; }
    setSeqIndex(next);
    setPhase(PHASE.PRIME);
  }, [currentItem, seqIndex, secuencia.length]);

  const handleRetry = () => {
    setSeqIndex(0); setResponses([]); setShowResults(false); setPhase(PHASE.PRIME);
  };

  if (error) return <ErrorScreen message={error} onBack={goBack} />;
  if (phase === PHASE.LOADING || !config) return <LoadingScreen />;

  if (phase === PHASE.DONE) {
    const byFacilitador = { SI: { correctos: 0, sumRt: 0, total: 0 }, NO: { correctos: 0, sumRt: 0, total: 0 } };
    responses.forEach(({ facilitador, correcto, rt }) => {
      const key = facilitador === 'SI' ? 'SI' : 'NO';
      byFacilitador[key].total++;
      if (correcto) { byFacilitador[key].correctos++; byFacilitador[key].sumRt += rt; }
    });

    const registros = responses.map((r) => ({
      pregunta:        r.valor,
      respuesta:       r.correcto ? r.respuesta : (r.respuesta === 'SI' ? 'NO' : 'SI'),
      respuestaUsuario:r.respuesta,
      correcta:        r.correcto,
      tiempoRespuesta: String(r.rt),
    }));

    return (
      <DoneScreen conclusiones={showResults ? null : conclusiones} onRetry={handleRetry} onBack={goBack}>
        <SaveResults tipo={`P${testId}`} registros={registros} />
        {showResults ? (
          <div className="mt-6 w-full max-w-md mx-auto text-left">
            <h3 className="text-lg font-semibold mb-3 text-center">Resultados por facilitador</h3>
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-slate-100">
                  <th className="px-3 py-2 text-left font-medium text-slate-600">Facilitador</th>
                  <th className="px-3 py-2 text-right font-medium text-slate-600">Correctos</th>
                  <th className="px-3 py-2 text-right font-medium text-slate-600">RT prom. (ms)</th>
                </tr>
              </thead>
              <tbody>
                {['SI', 'NO'].map((key) => (
                  <tr key={key} className="border-t border-slate-200">
                    <td className="px-3 py-2 text-slate-700">{key}</td>
                    <td className="px-3 py-2 text-right text-slate-700">{byFacilitador[key].correctos} / {byFacilitador[key].total}</td>
                    <td className="px-3 py-2 text-right text-slate-700">
                      {byFacilitador[key].correctos > 0
                        ? Math.round(byFacilitador[key].sumRt / byFacilitador[key].correctos)
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex justify-center mt-4">
              <button
                onClick={() => setShowResults(false)}
                className="px-4 py-2 rounded-full border border-slate-300 text-slate-700 text-sm bg-white hover:bg-slate-50 cursor-pointer uppercase"
              >
                OCULTAR RESULTADOS
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setShowResults(true)}
            className="px-4 py-2 rounded-full border border-slate-300 text-slate-700 text-sm bg-white hover:bg-slate-50 mt-2 cursor-pointer"
          >
            QUIERO SABER MIS RESULTADOS
          </button>
        )}
      </DoneScreen>
    );
  }

  if (!currentItem) { setPhase(PHASE.DONE); return null; }

  const facilitadorHtml = currentItem.valor_enmascardo || currentItem.Valor;

  return (
    <TestLayout current={seqIndex + 1} total={secuencia.length} onExit={handleExit}>
      {phase === PHASE.PRIME && (
        <div className="flex items-center justify-center min-h-[12rem]">
          <SafeHtml as="div" html={facilitadorHtml} className={WORD_CLASS} />
        </div>
      )}

      {phase === PHASE.MASK && <MaskedWord html={facilitadorHtml} />}

      {phase === PHASE.RESPONSE && (
        <div className="flex flex-col items-center">
          <SafeHtml
            as="div"
            html={currentItem.Valor}
            className={`${WORD_CLASS} mb-2 min-h-[4rem] flex items-center`}
          />
          <p className="text-slate-500 text-sm mb-2">¿Reconoció esta palabra?</p>
          <YesNoButtons onYes={() => handleResponse(true)} onNo={() => handleResponse(false)} />
        </div>
      )}
    </TestLayout>
  );
}
